import { useState } from 'react';
import { cn } from '@/utils/cn';
import { ChevronDownIcon, ClockIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { Badge } from '@/components/ui';
import type { PhaseScore, RegionPhaseScore, ComponentDetail } from '@/types/api';
import { normalizeScore, getScoreVariant, getTrajectoryDisplay } from './compactPhaseScoreHelpers';

interface CompactPhaseScoresProps {
  /** Array of phase scores to display */
  phaseScores: PhaseScore[];
  /** Callback when a phase is clicked (for video seeking) */
  onPhaseClick?: (phase: PhaseScore) => void;
  /** Additional class names */
  className?: string;
  /**
   * PR 1 — Role-gating: when false, numeric score badges and the BDS/JAS
   * expansion panel are hidden. Athletes see phase name + color indicator only.
   * Defaults to true so all existing call sites are unaffected.
   */
  showScores?: boolean;
}

/**
 * Get the primary score for a phase
 * Tries form_score, spatial_score, score, then overall_phase_score
 */
function getPrimaryScore(phase: PhaseScore): number {
  const score =
    phase.form_score ??
    phase.spatial_score ??
    phase.score ??
    phase.overall_phase_score ??
    0;
  return normalizeScore(score);
}

/**
 * Get background color classes based on score
 */
function getScoreBgClasses(score: number): string {
  if (score >= 80) return 'bg-success/10 border-success/30 hover:bg-success/20';
  if (score >= 60) return 'bg-warning/10 border-warning/30 hover:bg-warning/20';
  return 'bg-danger/10 border-danger/30 hover:bg-danger/20';
}

function CompactPhaseScores({
  phaseScores,
  onPhaseClick,
  className,
  showScores = true,
}: CompactPhaseScoresProps) {
  const [expandedPhaseId, setExpandedPhaseId] = useState<string | null>(null);

  if (!phaseScores || phaseScores.length === 0) {
    return null;
  }

  const handlePhaseClick = (phase: PhaseScore) => {
    const phaseId = phase.phase_id || `phase-${phase.phase_index}`;

    // Toggle expansion — only when scores are shown (coach view)
    if (showScores) {
      setExpandedPhaseId(expandedPhaseId === phaseId ? null : phaseId);
    }

    // Trigger callback for video seeking (always — athletes can seek to phases too)
    onPhaseClick?.(phase);
  };

  return (
    <div className={cn('space-y-3', className)}>
      {/* Phase tabs */}
      <div className="flex flex-wrap gap-2">
        {phaseScores.map((phase, index) => {
          const phaseId = phase.phase_id || `phase-${index}`;
          const phaseName = phase.phase_name || phase.phase_label || `Phase ${index + 1}`;
          const score = getPrimaryScore(phase);
          const isExpanded = expandedPhaseId === phaseId;

          return (
            <button
              key={phaseId}
              onClick={() => handlePhaseClick(phase)}
              className={cn(
                'flex items-center gap-2 px-4 py-2.5 rounded-lg border',
                'font-medium text-sm',
                'transition-all duration-200',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
                getScoreBgClasses(score),
                showScores && isExpanded && 'ring-2 ring-accent shadow-md'
              )}
            >
              <span className="truncate max-w-[120px]">{phaseName}</span>
              {/* Score badge and expand chevron — coach/admin only */}
              {showScores && (
                <>
                  <Badge variant={getScoreVariant(score)} size="sm">
                    {Math.round(score)}%
                  </Badge>
                  <ChevronDownIcon
                    className={cn(
                      'w-4 h-4 transition-transform duration-200',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </>
              )}
            </button>
          );
        })}
      </div>

      {/* Expanded phase details — coach/admin only (BDS/JAS per-bone grid) */}
      {showScores && expandedPhaseId && (
        <PhaseDetails
          phase={phaseScores.find(
            (p) =>
              (p.phase_id || `phase-${p.phase_index}`) === expandedPhaseId
          )}
        />
      )}
    </div>
  );
}

interface PhaseDetailsProps {
  phase?: PhaseScore;
}

function PhaseDetails({ phase }: PhaseDetailsProps) {
  if (!phase) return null;

  const phaseName = phase.phase_name || phase.phase_label || 'Phase';
  const phaseId = phase.phase_id;
  const phaseLabel = phase.phase_label || phase.phase_name;
  const timingScore = normalizeScore(phase.timing_score ?? phase.temporal_score);
  const hasTimingScore = phase.timing_score !== undefined || phase.temporal_score !== undefined;

  // Filter region scores to only show those matching this phase
  // Each RegionPhaseScore has phase_id and phase_label fields indicating which phase it belongs to
  const allRegionScores = phase.region_scores || [];
  const regionScores = allRegionScores.filter((region) => {
    // Match by phase_id if both have it
    if (phaseId && region.phase_id) {
      return region.phase_id === phaseId;
    }
    // Match by phase_label
    if (region.phase_label) {
      // Direct match with phase_label or phase_name
      if (phaseLabel && region.phase_label === phaseLabel) {
        return true;
      }
      if (phaseName && region.phase_label === phaseName) {
        return true;
      }
      // Check if phase_name contains the region's phase_label (e.g., "Lunge Execution" contains "Lunge")
      if (phaseName && phaseName.toLowerCase().includes(region.phase_label.toLowerCase())) {
        return true;
      }
      // Check if region's phase_label contains the phase_name (e.g., "Lunge" in "Lunge Execution")
      if (phaseName && region.phase_label.toLowerCase().includes(phaseName.toLowerCase())) {
        return true;
      }
      return false;
    }
    // If no phase identifiers on region, include all (legacy data)
    return true;
  });

  // Compute form score after regionScores so we can check if the phase has any regions.
  // A phase with no annotated regions produces form_score=0 automatically — this does NOT
  // mean the athlete performed poorly; there is simply nothing to score against.
  const rawFormScore = phase.form_score ?? phase.spatial_score ?? phase.score;
  const formScore = normalizeScore(rawFormScore);
  const formScoreIsEmpty = (rawFormScore === 0 || rawFormScore === undefined) && regionScores.length === 0;

  return (
    <div
      className={cn(
        'p-4 rounded-lg border bg-dark-card border-border-subtle',
        'animate-in slide-in-from-top-2 duration-200'
      )}
    >
      {/* Phase header */}
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-text-primary">{phaseName}</h4>
        {phase.start_frame !== undefined && phase.end_frame !== undefined && (
          <span className="text-xs text-text-muted">
            Frames {phase.start_frame} - {phase.end_frame}
          </span>
        )}
      </div>

      {/* Score summary */}
      <div className="flex flex-wrap gap-4 mb-4">
        <div className="flex items-center gap-2">
          <ChartBarIcon className="w-5 h-5 text-text-muted" />
          <div>
            <p className="text-xs text-text-muted">Form Score</p>
            {formScoreIsEmpty ? (
              <p className="text-lg font-bold text-text-muted" title="No regions annotated for this phase">
                N/A
              </p>
            ) : (
              <p className={cn(
                'text-lg font-bold',
                formScore >= 80 ? 'text-success' :
                formScore >= 60 ? 'text-warning' : 'text-danger'
              )}>
                {Math.round(formScore)}%
              </p>
            )}
          </div>
        </div>

        {hasTimingScore && (
          <div className="flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-text-muted" />
            <div>
              {/* Timing is DIAGNOSTIC, not a score component: phase tempo was
                  retired from scoring (temporal_weight is pinned to 0.0, and
                  the scoring-aspects registry stitch test keeps it that way).
                  Rendering it beside real components without saying so invites
                  an athlete to read "Timing 68 / Overall 85" as a contradiction
                  when the first number participates in nothing. Colour is
                  neutral for the same reason — red/green implies consequence. */}
              <p className="text-xs text-text-muted">Timing</p>
              <p className="text-lg font-bold text-text-secondary">
                {Math.round(timingScore)}%
              </p>
              <p className="text-[10px] text-text-muted">
                Information only — doesn&apos;t affect the score
              </p>
            </div>
          </div>
        )}

        {phase.timing_ratio !== undefined && (
          <div className="flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-text-muted" />
            <div>
              <p className="text-xs text-text-muted">Timing Ratio</p>
              <p className="text-lg font-bold text-text-primary">
                {phase.timing_ratio.toFixed(2)}x
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Region scores */}
      {regionScores.length > 0 && (
        <div>
          <p className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2">
            Region Scores
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {regionScores.map((region, idx) => (
              <RegionScoreCard key={region.region_id || idx} region={region} />
            ))}
          </div>
        </div>
      )}

      {/* Component details with trajectory quality indicators */}
      {phase.component_details && phase.component_details.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2">
            Movement Quality by Body Part
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {phase.component_details.map((component, idx) => (
              <ComponentDetailRow key={idx} component={component} />
            ))}
          </div>
        </div>
      )}

      {/* Feedback if available */}
      {phase.feedback && phase.feedback.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border-subtle">
          <p className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2">
            Feedback
          </p>
          <ul className="space-y-1">
            {phase.feedback.map((tip, idx) => (
              <li key={idx} className="text-sm text-text-secondary flex items-start gap-2">
                <span className="text-accent mt-1">•</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Trajectory quality indicator (getTrajectoryDisplay moved to
// compactPhaseScoreHelpers.ts — see import above)
// ============================================================================

interface TrajectoryIndicatorProps {
  factor: number | null;
}

/**
 * Small inline trajectory quality indicator.
 * Shows a colored dot and percentage text with a tooltip on hover.
 * Renders nothing when factor is null and we want to suppress the N/A entry.
 */
function TrajectoryIndicator({ factor }: TrajectoryIndicatorProps) {
  const { color, dotColor, label, tooltip } = getTrajectoryDisplay(factor);

  return (
    <span
      className="flex items-center gap-1 cursor-help shrink-0"
      title={tooltip}
      aria-label={`Trajectory match: ${label}`}
    >
      {/* Colored status dot */}
      <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', dotColor)} />
      {/* Percentage text */}
      <span className={cn('text-xs tabular-nums', color)}>{label}</span>
    </span>
  );
}

interface ComponentDetailRowProps {
  component: ComponentDetail;
}

/**
 * Single row in the component details table.
 * Shows the body-part label, score type badge, score bar + value,
 * and a trajectory quality indicator.
 */
function ComponentDetailRow({ component }: ComponentDetailRowProps) {
  const { label, type, avg_score, trajectory_factor } = component;
  const scoreVariant = getScoreVariant(avg_score);

  const variantStyles = {
    success: 'bg-success/10 border-success/30',
    warning: 'bg-warning/10 border-warning/30',
    error: 'bg-danger/10 border-danger/30',
  };

  const barColors = {
    success: 'bg-success',
    warning: 'bg-warning',
    error: 'bg-danger',
  };

  // Only show the trajectory indicator when factor is explicitly provided
  // (i.e., not null — null means data unavailable)
  const showTrajectory = trajectory_factor !== null;

  return (
    <div
      className={cn(
        'flex flex-col gap-1.5 p-2 rounded-md border',
        variantStyles[scoreVariant]
      )}
    >
      {/* Top row: label + type badge + trajectory indicator */}
      <div className="flex items-center justify-between gap-2 min-w-0">
        <span className="text-sm font-medium text-text-primary truncate">{label}</span>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Score type tag */}
          <span className="text-[10px] uppercase tracking-wider text-text-muted font-semibold px-1 py-0.5 rounded bg-dark-elevated-2">
            {type === 'bds' ? 'Bone' : 'Joint'}
          </span>
          {/* Score badge */}
          <Badge variant={scoreVariant} size="sm">
            {Math.round(avg_score)}%
          </Badge>
        </div>
      </div>

      {/* Bottom row: score bar + trajectory match */}
      <div className="flex items-center gap-2">
        {/* Score progress bar */}
        <div className="flex-1 h-1 bg-dark-elevated-2 rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all duration-300', barColors[scoreVariant])}
            style={{ width: `${Math.min(100, Math.max(0, avg_score))}%` }}
          />
        </div>
        {/* Trajectory indicator — shown when data is available */}
        {showTrajectory ? (
          <TrajectoryIndicator factor={trajectory_factor} />
        ) : (
          /* When trajectory data is null, show a subtle N/A so layout is consistent */
          <span
            className="text-xs text-text-muted/50 tabular-nums"
            title="Trajectory data unavailable for this component (static phase or motion quality not computed). Trajectory quality measures how similar the movement PATH is, not just the final position."
          >
            —
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Region score card (unchanged)
// ============================================================================

interface RegionScoreCardProps {
  region: RegionPhaseScore;
}

function RegionScoreCard({ region }: RegionScoreCardProps) {
  const score = normalizeScore(region.weighted_score ?? 0);
  const variant = getScoreVariant(score);

  const variantStyles = {
    success: 'bg-success/10 border-success/30',
    warning: 'bg-warning/10 border-warning/30',
    error: 'bg-danger/10 border-danger/30',
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between p-2 rounded-md border',
        variantStyles[variant]
      )}
    >
      <span className="text-sm font-medium text-text-primary truncate">
        {region.region_label || region.region_id}
      </span>
      <Badge variant={variant} size="sm">
        {Math.round(score)}%
      </Badge>
    </div>
  );
}

export default CompactPhaseScores;
export { ComponentDetailRow, TrajectoryIndicator };
// getTrajectoryDisplay, getScoreVariant, normalizeScore moved to
// compactPhaseScoreHelpers.ts (react-refresh/only-export-components).
